import { PRoute } from "src/app/models/p-route";
import { convertPubRouteToLRoute, convertSRouteToLRoute, getRouteDescriptionForLanguage, getRouteNameForLanguage } from "./utils-route";
import { MapPublicRoute, MapRouteCollection } from "src/dto.generated/api";
import { getRouteCollectionDescriptionForLanguage, getRouteCollectionNameForLanguage } from "./utils-route-collections";
import { getSRouteDescriptionForLanguage, getSRouteNameForLanguage, getSRoutePlannerOptions } from "./utils-server-route";


// create JsonLd for app
export function createJsonLdForApp(langCode: string) {

    let description: string;
    if (langCode === "en") {
        description = "Trip4You-Maps is a progressive web app for outdoor route planning and GPS navigation." +
            " Ideal for cycling and hiking tours - it helps you discover, plan, and follow scenic paths across regions.";
    }
    if (langCode === "de") {
        description = "Trip4You-Maps ist eine progressive Web-App für Outdoor-Routenplanung und GPS-Navigation." +
            " Ideal für Rad- und Wandertouren - sie hilft dir malerische Wege in verschiedenen Regionen zu entdecken, zu planen und zu verfolgen.";
    }

    const jsonLd = {
        "@context": "https://schema.org/",
        "@type": "WebApplication",
        "@id": `https://maps.trip4you.net/#${langCode}`,
        "inLanguage": langCode,
        "name": "Trip4You-Maps",
        "description": description,
        "url": "https://maps.trip4you.net/",
        "applicationCategory": "TravelApplication",
        "operatingSystem": "Cross-Platform",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
        },
    };
    return jsonLd;
}

// create JsonLd for info-page
export function createJsonLdForInfoPages(index: number, name: string, description: string, langCode: string) {

    // about
    if (index === 0) {
        const jsonLd = {
            "@context": "https://schema.org/",
            "@type": "AboutPage",
            "name": name,
            "description": description,
            "url": "https://maps.trip4you.net/about/" + langCode
        }
        return jsonLd;
    }
    // FAQ
    if (index === 1) {
        const jsonLd = {
            "@context": "https://schema.org/",
            "@type": "FAQPage",
            "name": name,
            "description": description,
            "url": "https://maps.trip4you.net/faq/" + langCode
        }
        return jsonLd;
    }
    // terms-of-use
    if (index === 3) {
        const jsonLd = {
            "@context": "https://schema.org/",
            "@type": "TermsOfService",
            "name": name,
            "description": description,
            "url": "https://maps.trip4you.net/terms-of-use/" + langCode
        }
        return jsonLd;
    }
    // feedback
    if (index === 4) {
        const jsonLd = {
            "@context": "https://schema.org/",
            "@type": "TermsOfService",
            "name": name,
            "description": description,
            "url": "https://maps.trip4you.net/feedback/" + langCode
        }
        return jsonLd;
    }
    // contact
    if (index === 5) {
        const jsonLd = {
            "@context": "https://schema.org/",
            "@type": "ContactPage",
            "name": name,
            "description": description,
            "url": "https://maps.trip4you.net/contact/" + langCode
            // §todo organisation
        }
        return jsonLd;
    }
    // more apps
    if (index === 6) {
        const jsonLd = {
            "@context": "https://schema.org/",
            "@type": "CollectionPage",
            "name": name,
            "description": description,
            "mainEntity": [
                {
                    "@type": "WebApplication",
                    "name": "Trip4You-Explore",
                    "applicationCategory": "TravelApplication",
                    "operatingSystem": "Cross-Platform",
                    "url": "https://trip4you.net/explore",
                    "image": "https://trip4you.net/assets/icons_logo/icon-256x256.png",
                    "description": "App exploring travel information"
                },
                {
                    "@type": "WebApplication",
                    "name": "Trip4You-Planner",
                    "applicationCategory": "WeatherApplication",
                    "operatingSystem": "Cross-Platform",
                    "url": "https://trip4you.net/planner",
                    "image": "https://trip4you.net/assets/icons_logo/icon-256x256.png",
                    "description": "App for planning individual trips"
                }
            ]
        }
        return jsonLd;
    }
    return;
}

// create JsonLd for route
export function createJsonLdForRoute(route: MapPublicRoute, langCode: string) {

    const name = getSRouteNameForLanguage(route, langCode);
    const description = getSRouteDescriptionForLanguage(route, langCode);
    let touristType = "Bike Tour";
    const plannerOptions = getSRoutePlannerOptions(route);
    const moveType = plannerOptions.moveType;
    if (moveType === "mtb") { touristType = "Mountain Bike Tour"; }
    if (moveType === "bike-road") { touristType = "Road Cycling Tour"; }
    if (moveType === "hiking") { touristType = "Hiking Tour"; }
    if (moveType === "walking") { touristType = "Walking Tour"; }
    if (moveType === "car") { touristType = "Road Trip"; }
    const distance = Math.round(route.routeLength * 0.001) + "km";
    const durationH = Math.floor(route.duration / 3600);
    const durationM = Math.floor(route.duration / 60) - durationH * 60;
    const duration = "PT" + durationH + "H" + durationM + "M";

    const wayPoints = JSON.parse(route.wayPoints);
    const itinerary: any[] = [];
    for (const wp of wayPoints) {
        // console.log("maps:createJsonLdForRoute-wp", wp);
        const wpLd = {
            "@type": "Place",
            "name": wp.name,
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": wp.coordLat,
                "longitude": wp.coordLon
            }
        }
        itinerary.push(wpLd);
    }
    const jsonLd = {
        "@context": "https://schema.org/",
        "@type": "TouristTrip",
        "@id": `https://maps.trip4you.net/route/${langCode}/${route.routeId}`,
        "inLanguage": langCode,
        "name": name,
        "description": description,
        "touristType": touristType,
        "url": `https://maps.trip4you.net/route/${langCode}/${route.routeId}`,
        "distance": distance,
        "estimatedDuration": duration,
        // "image":
        "provider": {
            "@type": "Organization",
            "name": "Trip4You Maps",
            "url": "https://maps.trip4you.net",
            "logo": "https://maps.trip4you.net/assets/icons_logo/icon-256x256.png",
        }
        // "itinary": itinerary,
    };
    return jsonLd;
}

// create JsonLd for collection
export function createJsonLdForCollection(collection: MapRouteCollection, langCode: string) {

    const description = getRouteCollectionDescriptionForLanguage(collection, langCode);
    const name = getRouteCollectionNameForLanguage(collection, langCode);
    const routeItems: any[] = [];
    // for(const rId of collection.routeIds){
    // }

    const jsonLd = {
        "@context": "https://schema.org/",
        "@type": "ItemList",
        "@id": `https://maps.trip4you.net/route/${langCode}/${collection.collectionId}`,
        "inLanguage": langCode,
        "name": name,
        "description": description,
        "provider": {
            "@type": "Organization",
            "name": "Trip4You Maps",
            "url": "https://maps.trip4you.net",
            "logo": "https://maps.trip4you.net/assets/icons_logo/icon-256x256.png",
        }
        // "itemListElement": []
    };
    return jsonLd;
}

// export function createRelAlternateForRoute(route: MapPublicRoute, langCode: string) {

//     const nameDe = route.nameDe;
//     const nameEn = route.nameEn;
//     if (nameDe && nameEn) {
//         this.seo.setAlternateLink
//     }

// }
// export function createRelAlternateForCollection(collection: MapRouteCollection, langCode: string) {

// }
// export function createRelAlternateForInfoPages(type: string, langCode: string) {

// }